import { z } from "zod";
import { isReadPlatform } from "./channels.js";
import { numbersIn } from "./help/assistant.js";
import { numbersInWords } from "./scene.js";

/**
 * The marketing review ([35](../../../docs/35-the-marketing-manager.md), ADR 0063): the first turn of the loop the
 * product is being rebuilt around — evidence, a proposal, a decision, the work, and a look at what came of it.
 *
 * Three hands, and each one only does its own part:
 *
 *  - **Code gathers the evidence** (`ReviewEvidence`), free, from rows Joogoos already keeps: the website count, the
 *    letters, the posts, Search Console, and the decisions already taken. It also says what it **cannot** see
 *    (`Gap`), because a review that is quiet about a missing integration reads as a quiet week.
 *  - **A model proposes** (`marketing_review`): what happened, what is uncertain, and at most three actions from a
 *    closed menu (`ActionKind`), each mapped to a tool Joogoos already has.
 *  - **Code decides what survives** (`applyReview`): a figure the evidence does not hold is dropped, a metric that
 *    cannot be counted is replaced, the price is worked out here and never by the model, and thin evidence makes the
 *    action a bounded test rather than a strategy. Then the owner decides; nothing is spent, sent or published by
 *    saying yes.
 *
 * Nothing in here names a customer. It is written for a bakery and for a software company alike (docs/26 §no special
 * cases): the first business it was tried on is only the first.
 */

/* ── the window and the thresholds ─────────────────────────────────────────────────────────────────────────── */

/** A review reads the last seven days: a week is the rhythm the owner asked for, and the one a small business keeps. */
export const REVIEW_WINDOW_DAYS = 7;

/**
 * Below these, a week is a handful of events and any pattern in it is luck: the action is a **bounded test**, never a
 * strategy (the owner, 2026-10-02: "with sparse data, propose a bounded experiment rather than claim a winning
 * strategy"). One source with enough is enough — a business writing thirty letters a week has something to learn from
 * even while its website is quiet.
 */
export const ENOUGH_VISITORS = 100;
export const ENOUGH_ENQUIRIES = 10;
export const ENOUGH_LETTERS = 30;

/** When the outcome is looked at, in days after the yes. A test is never left running longer than four weeks. */
export const REVIEW_AFTER_DAYS = [7, 14, 21, 28, 42] as const;
export const TEST_AFTER_DAYS_MAX = 28;
export const DEFAULT_REVIEW_AFTER_DAYS = 14;

/**
 * How much one workspace may ask, as counts rather than money: the money is the person's caps, through `runCall`
 * (13). These bound a bug or a stuck finger, and a conversation that has stopped deciding anything.
 */
export const REVIEW_LIMITS = { reviewsPerDay: 3, turnsPerReview: 12, turnsPerDay: 30, said: 1_000 } as const;

/** The day a counter goes quiet enough to be worth saying: three days with nothing at all is not a quiet week. */
export const QUIET_DAYS = 3;

/* ── what code cannot see ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * Everything a review can say it could not see, closed so the screen can send each one to where it is fixed and a
 * later desk panel can count them across workspaces (35 §product improvement). A model never adds one: what is
 * missing is a fact about rows, and code knows it better.
 */
export const Gap = z.enum([
  "website_not_given",
  "counter_not_installed",
  "counter_quiet",
  "window_partial",
  "enquiries_none_seen",
  "goal_not_said",
  "mailbox_not_connected",
  "replies_unseen",
  "letters_rehearsed",
  "post_metrics_missing",
  "search_not_connected",
  "search_broken",
  "product_events_missing",
]);
export type Gap = z.infer<typeof Gap>;

/** Where a gap is fixed: a tab, and on Business the step. Null for a gap nobody can fix from the app today. */
export type GapWhere = { tab: "Business" | "Customers" | "SEO"; step?: "about" | "site" | "customers" | "channels" } | null;

/** The sentence a gap is said as, and where it is fixed. The owner reads these; they say what is not counted, plainly. */
export function gapSays(gap: Gap, e?: Pick<ReviewEvidence, "website">): { says: string; where: GapWhere } {
  switch (gap) {
    case "website_not_given":
      return { says: "Joogoos does not know your website's address, so it cannot count who visits it.", where: { tab: "Business", step: "site" } };
    case "counter_not_installed":
      return { says: "Your website is not being counted yet: the line of script is not in it, or nobody has visited since it went in.", where: { tab: "Business", step: "site" } };
    case "counter_quiet": {
      const days = e?.website.lastEventDaysAgo;
      return { says: `Nothing has been counted on your website for ${days ?? "several"} days. The line of script may have come out of the site.`, where: { tab: "Business", step: "site" } };
    }
    case "window_partial":
      return { says: "Counting started during this week, so the week is not a whole one.", where: null };
    case "enquiries_none_seen":
      // The links need nothing doing and the form does, so the sentence says which is which — a review that read
      // "mark your phone and email links" sent an owner to mark what was already counted — and names the step,
      // which since 2026-10-05 shows the mark on a form (the first pilot round: it used to show nothing).
      return {
        says: "Nobody has been seen getting in touch through your website. Phone, email and WhatsApp links are counted by themselves; a contact form or a button is invisible to Joogoos until it is marked with data-joogoos-goal, which Business, under Website & analytics, shows how to do.",
        where: { tab: "Business", step: "site" },
      };
    case "goal_not_said":
      return { says: "You have not said what you want more of, so this review had to guess what matters.", where: { tab: "Business", step: "about" } };
    case "mailbox_not_connected":
      // The mailbox is connected on the Business page, which the Customers tab itself points at.
      return { says: "No mailbox is connected, so no letter can be sent and none is counted.", where: { tab: "Business", step: "customers" } };
    case "replies_unseen":
      // Nothing the owner can fix from the app: since 2026-09-27 a mailbox is asked to send and never to read (14), so
      // this is said and never proposed as a fix. Since 2026-10-06 a person can say a company replied, and that is
      // counted — so the sentence says the count is theirs, rather than that there is none.
      return { says: "Your mailbox can send but not read, so Joogoos counts a reply only when you press They replied on that company in Customers. A reply you answered in your inbox and never marked is not in this count.", where: null };
    case "letters_rehearsed":
      // Said for every value of the box's switch but `on`: a rehearsal delivers to the owner's own address, an
      // allow-list refuses everybody else, and off sends nothing — in none of them did a company receive a letter.
      // In words that do not name the machine (ADR 0045), and with no digit, which the review's prompt relies on.
      return { says: "Letters from here do not reach companies yet: Joogoos only rehearses them for now, delivering each to your own address or not at all.", where: null };
    case "post_metrics_missing":
      return { says: "Joogoos cannot read how a post did on every network yet, so some of your posts have no views or likes to go on.", where: null };
    case "search_not_connected":
      return { says: "Google Search Console is not connected, so the searches that found you are not counted.", where: { tab: "SEO" } };
    case "search_broken":
      return { says: "Google Search Console stopped answering. Connect it again for the searches to be counted.", where: { tab: "SEO" } };
    case "product_events_missing":
      return { says: "What happens after somebody gets in touch — a sign-up, an order, an upload in your own system — is not reported to Joogoos, so this review cannot follow it.", where: null };
  }
}

/* ── the evidence ──────────────────────────────────────────────────────────────────────────────────────────── */

const count = z.number().int().min(0);

/**
 * What code read for one workspace and one week, stored with the review so the review can always be read against
 * what it was written from, and given to the model as the only facts it has.
 *
 * Counts, never people: nothing here names a visitor, a company or an address (ADR 0035, ADR 0037). Every figure is
 * a count of rows Joogoos keeps, over the window, with the caveat the row deserves written beside it in `gaps`.
 */
export const ReviewEvidence = z.object({
  window: z.object({
    from: z.string(),
    to: z.string(),
    days: z.number().int().min(1),
    /** "25 September to 1 October", so a sentence can name the days and the numbers in it are ones the evidence holds. */
    label: z.string(),
  }),
  /** What the owner wants more of, in their own words (`facts.works`), or null when the page does not say. */
  goal: z.string().nullable(),
  website: z.object({
    state: z.enum(["none", "not_installed", "counting"]),
    domain: z.string().nullable(),
    /** The day the first visit was counted, as words. */
    countingSince: z.string().nullable(),
    lastEventDaysAgo: z.number().int().min(0).nullable(),
    /** Counting started inside the window, so the week is not whole. */
    partial: z.boolean(),
    /** People, counted once a day each (ADR 0035): over a week this is person-days, not people. */
    visitors: count,
    views: count,
    /** Getting in touch, as events — two presses by one person are two. */
    enquiries: count,
    /** The week before, for "more than last week". Null when there is no whole week before to compare with. */
    before: z.object({ visitors: count, enquiries: count }).nullable(),
    /** Enquiries by how: `call`, `email`, `whatsapp`, `book`, or a name the site gave its own form or button. */
    goals: z.array(z.object({ goal: z.string(), events: count })),
    sources: z.array(z.object({ name: z.string(), visitors: count, enquiries: count })),
    pages: z.array(z.object({ path: z.string(), visitors: count, enquiries: count })),
    /** Enquiries since the counter went in: zero here is what `enquiries_none_seen` is made of. */
    enquiriesEver: count,
  }),
  outreach: z.object({
    state: z.enum(["not_set_up", "set_up"]),
    /**
     * Whether a reply is noticed, and how: by reading the thread (`reads_replies`), through a reply address of
     * Joogoos's own on a mailbox that can only send (`replies_by_address`, ADR 0044's fallback, chosen for send-only
     * mailboxes on 2026-10-06), or only when a person presses *They replied* (`send_only`).
     */
    mailbox: z.enum(["none", "send_only", "replies_by_address", "reads_replies"]),
    /** This server's own switch for letters (`OUTREACH_SENDING`): only `on` reaches a real company. */
    sending: z.enum(["off", "allowlist", "rehearsal", "on"]),
    /** Whether the Customers loop is running on its own (ADR 0039). */
    loop: z.boolean(),
    week: z.object({ found: count, judgedGo: count, drafted: count, sent: count, replies: count, meetings: count }),
    total: z.object({ companies: count, sent: count, replies: count, meetings: count }),
  }),
  posts: z.object({
    channels: z.array(z.object({ platform: z.string(), state: z.enum(["connected", "paused", "needs_reconnect"]) })),
    week: z.object({ published: count, failed: count, scheduled: count, drafts: count }),
    /** Posts and videos finished in the Studio during the window. */
    made: count,
    /** What the networks say of the posts published in the window, at the last nightly read (25). */
    read: z.object({ posts: count, views: count, likes: count, comments: count }),
  }),
  search: z.object({
    state: z.enum(["no_site", "not_connected", "connected", "broken"]),
    pulledDaysAgo: z.number().int().min(0).nullable(),
    /** Google's last 28 days as of the last pull: a snapshot, never this week's own number. */
    clicks: count,
    impressions: count,
  }),
  /** The decisions already taken, newest first, so the review does not propose last week's no again. */
  past: z.array(z.object({
    title: z.string(),
    kind: z.string(),
    state: z.enum(["proposed", "approved", "rejected"]),
    decidedOn: z.string().nullable(),
    reason: z.string().nullable(),
    outcome: z.string().nullable(),
  })).max(5),
  gaps: z.array(Gap),
  /** Too little in the week to learn a pattern from: the action becomes a bounded test (`ENOUGH_*`). */
  sparse: z.boolean(),
});
export type ReviewEvidence = z.infer<typeof ReviewEvidence>;

/** Too little to learn a pattern from, by the thresholds above. Pure, so the db and the tests agree on one reading. */
export function isSparse(e: Pick<ReviewEvidence, "website" | "outreach">): boolean {
  return !(e.website.visitors >= ENOUGH_VISITORS || e.website.enquiries >= ENOUGH_ENQUIRIES || e.outreach.week.sent >= ENOUGH_LETTERS);
}

/**
 * The gaps, read off the evidence by rule. Pure and ordered — the most blocking first — so the fallback action and
 * the screen agree on which one matters most.
 */
export function gapsOf(e: Omit<ReviewEvidence, "gaps" | "sparse">): Gap[] {
  const out: Gap[] = [];
  const w = e.website;
  if (w.state === "none") out.push("website_not_given");
  if (w.state === "not_installed") out.push("counter_not_installed");
  if (w.state === "counting" && (w.lastEventDaysAgo ?? 0) >= QUIET_DAYS) out.push("counter_quiet");
  if (w.state === "counting" && w.partial) out.push("window_partial");
  if (w.state === "counting" && w.enquiriesEver === 0) out.push("enquiries_none_seen");
  if (!e.goal?.trim()) out.push("goal_not_said");
  if (e.outreach.state === "set_up" && e.outreach.mailbox === "none") out.push("mailbox_not_connected");
  if (e.outreach.mailbox === "send_only") out.push("replies_unseen");
  if (e.outreach.state === "set_up" && e.outreach.sending !== "on") out.push("letters_rehearsed");
  if (e.posts.channels.some((c) => !isReadPlatform(c.platform)) || (e.posts.week.published && !e.posts.read.posts)) out.push("post_metrics_missing");
  if (e.search.state === "not_connected") out.push("search_not_connected");
  if (e.search.state === "broken") out.push("search_broken");
  // True of every business today: no endpoint takes an event from a business's own system (35 §the event contract).
  out.push("product_events_missing");
  return out;
}

/* ── what can be measured ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * How an action's outcome is counted, closed: each one is a count of rows Joogoos keeps, so the look afterwards is
 * the same query over a later window, and never something a model reads off a chart.
 */
export const Metric = z.enum([
  "site_visitors", "site_enquiries", "letters_sent", "replies", "meetings", "posts_published", "gap_closed", "none",
]);
export type Metric = z.infer<typeof Metric>;

export const METRIC_WORDS: Record<Metric, string> = {
  site_visitors: "people on your website",
  site_enquiries: "people getting in touch through your website",
  letters_sent: "letters sent",
  replies: "replies to your letters",
  meetings: "calls booked from your letters",
  posts_published: "posts published",
  gap_closed: "whether it is fixed",
  none: "nothing Joogoos can count",
};

/**
 * Whether a metric can be counted for this workspace at all. A reply count from letters that went to the owner's own
 * address is a zero that means nothing — the review would be measuring its own blindness.
 *
 * A mailbox that can only send counts replies since 2026-10-06: a reply is noticed through Joogoos's reply address
 * where the box has one, and otherwise when a person presses *They replied*, which is counted exactly as a read reply
 * is (`replies` in `packages/db/src/review.ts`). The second is the owner's word and can be short of the real number,
 * which is what the `replies_unseen` gap says beside it; it is never the zero it used to be, which said nothing.
 */
export function measurable(metric: Metric, e: Pick<ReviewEvidence, "website" | "outreach">): boolean {
  switch (metric) {
    case "site_visitors":
    case "site_enquiries":
      return e.website.state === "counting";
    case "letters_sent":
      return e.outreach.mailbox !== "none";
    case "replies":
    case "meetings":
      return e.outreach.mailbox !== "none" && e.outreach.sending === "on";
    case "posts_published":
    case "gap_closed":
    case "none":
      return true;
  }
}

/* ── what can be done ──────────────────────────────────────────────────────────────────────────────────────── */

/**
 * The menu, closed, and each item a tool Joogoos already has (35 §execution). A model choosing from it cannot invent
 * a feature, and every yes lands somewhere the owner already knows.
 *
 * - `fix_measurement`: close one of the gaps — the owner does it, where `gapSays` points. Free.
 * - `make_post`: a post or a video, started in the Studio from the idea. Free until Generate, which shows its price.
 * - `prepare_week`: Prepare my week in the Studio. Priced on its own button.
 * - `write_letters`: Customers — find, judge and draft. Nothing is sent until the owner presses Send.
 * - `write_article`: an article for the business's own site, on the SEO tab. Priced on its own button.
 * - `by_hand`: something Joogoos cannot do, said plainly so the owner can (docs/26: a workaround honestly said).
 * - `keep_going`: change nothing and look again — the honest answer to a week too thin to learn from.
 */
export const ActionKind = z.enum(["fix_measurement", "make_post", "prepare_week", "write_letters", "write_article", "by_hand", "keep_going"]);
export type ActionKind = z.infer<typeof ActionKind>;

/** Where approving each kind leads, and what the button there is called. */
export const ACTION_WHERE: Record<ActionKind, { tab: "Business" | "Studio" | "Customers" | "SEO" | null; press: string }> = {
  fix_measurement: { tab: "Business", press: "Fix it" },
  make_post: { tab: "Studio", press: "Open the post" },
  prepare_week: { tab: "Studio", press: "Open the Studio" },
  write_letters: { tab: "Customers", press: "Open Customers" },
  write_article: { tab: "SEO", press: "Open SEO" },
  by_hand: { tab: null, press: "Mark it done" },
  keep_going: { tab: null, press: "Mark it done" },
};

/** The kinds a sparse week may still propose as they are: fixing what cannot be seen is never a bet, and nor is waiting. */
const NEVER_A_TEST: ReadonlySet<ActionKind> = new Set(["fix_measurement", "keep_going"]);

/** What an action costs, worked out by code. `usd` null means "priced on its own button before anything runs". */
export interface ActionCost { usd: string | null; note: string }

/** One action, as it survived the checks and as the owner reads it. */
export interface ReviewAction {
  kind: ActionKind;
  /** What to do, as an instruction to the owner: "Mark the contact form so Joogoos counts it". */
  title: string;
  detail: string;
  why: string;
  /** What should happen if it works, said as a hope and never as a promise. */
  expected: string;
  metric: Metric;
  /** For `fix_measurement`, the gap it closes. */
  gap: Gap | null;
  reviewAfterDays: number;
  /** The owner's own time, as the model estimated it. Shown as "about", because it is a guess. */
  ownerMinutes: number;
  /** A bounded test rather than a strategy: the week was too thin to learn a pattern from. Code sets it. */
  experiment: boolean;
  cost: ActionCost;
}

/** The review as the owner reads it. `actions[0]` is the recommendation; the rest are what else was worth doing. */
export interface ReviewBody {
  happened: string[];
  uncertain: string[];
  diagnosis: string;
  actions: ReviewAction[];
}

/* ── the model's part ──────────────────────────────────────────────────────────────────────────────────────── */

/** What `marketing_review` is given: the business page, what the owner wants, and the evidence. Nothing else. */
export const MarketingReviewInput = z.object({
  /** The Business page as the Studio reads it, rivals left out. */
  business: z.string().max(20_000),
  evidence: ReviewEvidence,
});
export type MarketingReviewInput = z.infer<typeof MarketingReviewInput>;

/**
 * What the model answers. Loose on lengths on purpose — a sentence one character too long should be clipped by
 * `applyReview`, not fail a call somebody has already paid for — and closed on everything that names a thing.
 */
export const MarketingReviewOutput = z.object({
  happened: z.array(z.string()),
  uncertain: z.array(z.string()),
  diagnosis: z.string(),
  actions: z.array(z.object({
    kind: ActionKind,
    title: z.string(),
    detail: z.string(),
    why: z.string(),
    expected: z.string(),
    metric: Metric,
    /** For `fix_measurement`, the gap's name exactly as the evidence lists it; otherwise empty. */
    gap: z.string(),
    reviewAfterDays: z.number(),
    ownerMinutes: z.number(),
  })),
});
export type MarketingReviewOutput = z.infer<typeof MarketingReviewOutput>;

/* ── code decides ──────────────────────────────────────────────────────────────────────────────────────────── */

const clip = (s: string, n: number) => {
  const t = s.replace(/\s+/g, " ").trim();
  return t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t;
};

/** Numbers as digits with leading zeros and separators gone, so "01 October" and "1 October" are the same day. */
const normal = (n: string) => n.replace(/^0+(?=\d)/, "");
/**
 * The figures in a text, as digits: written in digits, or in words — "five people", "twenty-one visits" — because
 * plain English spells small counts out, and a thin week is made of small counts. "One" is left out: in "the one
 * thing worth doing" it is not a count, and refusing it would refuse half the sentences a review writes.
 */
export const reviewFigures = (text: string) =>
  new Set([...[...numbersIn(text)].map(normal), ...numbersInWords(text).filter((n) => n !== 1).map(String)]);

/** A share of anything, in digits or words. The evidence holds counts only, so a percentage is always a sum somebody did. */
const PERCENT = /%|\bper ?cent\b/i;
/** A ratio said as a word is a sum somebody did too: "visits doubled" is a claim no count in the evidence makes. */
const RATIO = /\b(doubled|tripled|trebled|quadrupled|halved|twice as|three times|half as|double the|triple the)\b/i;

/**
 * Every figure the evidence holds, as text. A sentence may use these and nothing else: "12 people came" is grounded
 * when 12 is a count here, and "a 40% rise" is not, because nobody counted it.
 */
export function groundedNumbers(e: ReviewEvidence): Set<string> {
  // The window's ends are moments the model is never shown: their year, month, hours and seconds are nobody's count,
  // and in October alone "10" would be held by every review. The label names the days a sentence may name.
  const { from: _from, to: _to, ...window } = e.window;
  return reviewFigures(JSON.stringify({ ...e, window }));
}

/** The numbers in `text` that are neither in `allowed` nor, when `small` is set, a small count like "three posts". */
export function inventedIn(text: string, allowed: Set<string>, small = false): string[] {
  const made = [...reviewFigures(text)].filter((n) => !allowed.has(n) && !(small && Number(n) >= 1 && Number(n) <= 12 && /^\d+$/.test(n)));
  return [...(PERCENT.test(text) ? ["a percentage"] : []), ...(RATIO.test(text) ? ["a ratio"] : []), ...made];
}

/** The nearest of the review days, and never past a test's four weeks. */
export function reviewDays(days: number, experiment: boolean): number {
  const choices = REVIEW_AFTER_DAYS.filter((d) => !experiment || d <= TEST_AFTER_DAYS_MAX);
  const want = Number.isFinite(days) ? days : DEFAULT_REVIEW_AFTER_DAYS;
  return choices.reduce((best, d) => (Math.abs(d - want) < Math.abs(best - want) ? d : best), choices[0]!);
}

/** The metric an action of this kind is counted by when the one it named cannot be counted here. */
function metricFor(kind: ActionKind, e: ReviewEvidence): Metric {
  const order: Record<ActionKind, Metric[]> = {
    fix_measurement: ["gap_closed"],
    make_post: ["site_visitors", "posts_published"],
    prepare_week: ["posts_published", "site_visitors"],
    write_letters: ["replies", "letters_sent"],
    write_article: ["site_visitors"],
    by_hand: ["site_enquiries", "none"],
    keep_going: ["site_visitors", "site_enquiries", "letters_sent", "none"],
  };
  return order[kind].find((m) => measurable(m, e)) ?? "none";
}

/** The costs code knows: a typical post and video from the Studio's own plan, so the review and Generate agree. */
export interface CostContext { postUsd: string; videoUsd: string }

/** What a kind costs to start, worked out here and never asked of the model. */
export function costOf(kind: ActionKind, ctx: CostContext): ActionCost {
  switch (kind) {
    case "fix_measurement":
    case "by_hand":
    case "keep_going":
      return { usd: "0.00", note: "Nothing to pay. It takes some of your time." };
    case "make_post":
      return { usd: ctx.postUsd, note: `About $${ctx.postUsd} for a post or $${ctx.videoUsd} for a video, shown again on Generate before anything runs.` };
    case "prepare_week":
      return { usd: null, note: "Priced on the Prepare my week button, and each piece again on Generate." };
    case "write_letters":
      return { usd: null, note: "Finding and drafting are priced on their own buttons. Nothing is sent until you press Send." };
    case "write_article":
      return { usd: null, note: "Priced on the SEO tab before anything is written." };
  }
}

/**
 * The action code proposes when the model's do not survive, or there is no model answer worth keeping. In order: the
 * most blocking gap, because a review that cannot see is the first thing to fix; then waiting, which costs nothing
 * and is the honest answer to a week with little in it.
 */
export function fallbackAction(e: ReviewEvidence, ctx: CostContext): ReviewAction {
  const blocking: Gap[] = ["website_not_given", "counter_not_installed", "counter_quiet", "enquiries_none_seen", "goal_not_said", "mailbox_not_connected"];
  const gap = blocking.find((g) => e.gaps.includes(g));
  if (gap) {
    return {
      kind: "fix_measurement", title: FIX_TITLE[gap] ?? "Fix what Joogoos cannot see", detail: gapSays(gap, e).says,
      why: "Until this is fixed, the next review will be as blind as this one.",
      expected: "The next review can count what it could not count this week.",
      metric: "gap_closed", gap, reviewAfterDays: 7, ownerMinutes: 10, experiment: false, cost: costOf("fix_measurement", ctx),
    };
  }
  return {
    kind: "keep_going", title: "Keep going as you are, and look again in two weeks",
    detail: "Change nothing this week. A fortnight more of the same is what makes the next review worth reading.",
    why: "There is too little in this week to say what is working.",
    expected: "Enough counted to tell a pattern from luck.",
    metric: metricFor("keep_going", e), gap: null, reviewAfterDays: DEFAULT_REVIEW_AFTER_DAYS, ownerMinutes: 0, experiment: false,
    cost: costOf("keep_going", ctx),
  };
}

const FIX_TITLE: Partial<Record<Gap, string>> = {
  website_not_given: "Tell Joogoos your website's address",
  counter_not_installed: "Put the counting line into your website",
  counter_quiet: "Check the counting line is still in your website",
  enquiries_none_seen: "Mark your contact form so Joogoos counts it",
  goal_not_said: "Say what you want more of",
  mailbox_not_connected: "Connect the mailbox your letters leave from",
};

/**
 * What survives the model's review. Whatever it answered, the owner gets:
 *
 *  - sentences about what happened whose every figure is in the evidence (the rest are dropped and logged);
 *  - at most three actions, each of a kind on the menu, counted by a metric that can be counted here, looked at on
 *    one of the review days, priced by code, and marked as a test whenever the week was thin;
 *  - always at least one action: `fallbackAction` when none of the model's survive.
 */
export function applyReview(raw: unknown, e: ReviewEvidence, ctx: CostContext): { body: ReviewBody; refused: string[] } {
  const refused: string[] = [];
  const parsed = MarketingReviewOutput.safeParse(raw);
  if (!parsed.success) return { body: { happened: [], uncertain: [], diagnosis: "", actions: [fallbackAction(e, ctx)] }, refused: ["the answer was not a review"] };
  const out = parsed.data;
  const allowed = groundedNumbers(e);

  const sentences = (list: string[], label: string) => list.map((s) => clip(s, 300)).filter((s) => {
    if (!s) return false;
    const made = inventedIn(s, allowed);
    if (made.length) refused.push(`${label}: ${made.join(", ")} is not in the evidence`);
    return !made.length;
  }).slice(0, 4);

  const happened = sentences(out.happened, "happened");
  const uncertain = sentences(out.uncertain, "uncertain");
  let diagnosis = clip(out.diagnosis, 400);
  if (inventedIn(diagnosis, allowed).length) { refused.push("diagnosis: a figure not in the evidence"); diagnosis = ""; }

  const actions: ReviewAction[] = [];
  const seen = new Set<string>();
  for (const a of out.actions.slice(0, 3)) {
    const title = clip(a.title, 120);
    if (!title) { refused.push(`${a.kind}: no title`); continue; }
    const key = `${a.kind}:${title.toLowerCase()}`;
    if (seen.has(key)) continue;

    let gap: Gap | null = null;
    if (a.kind === "fix_measurement") {
      const g = Gap.safeParse(a.gap.trim());
      if (!g.success || !e.gaps.includes(g.data)) { refused.push(`fix_measurement: ${clip(a.gap, 40) || "no gap"} is not one this review found`); continue; }
      // A gap nobody can fix from the app is said, never proposed: a yes to it would wait on the owner for ever.
      if (gapSays(g.data, e).where === null) { refused.push(`fix_measurement: ${g.data} cannot be fixed from Joogoos`); continue; }
      gap = g.data;
    }
    if (a.kind === "write_article" && e.website.state === "none") { refused.push("write_article: there is no website to publish on"); continue; }

    const experiment = e.sparse && !NEVER_A_TEST.has(a.kind);
    const days = a.kind === "fix_measurement" ? 7 : reviewDays(a.reviewAfterDays, experiment);
    const minutes = Math.max(0, Math.min(480, Math.round(Number.isFinite(a.ownerMinutes) ? a.ownerMinutes : 0)));
    const why = clip(a.why, 400);
    const detail = clip(a.detail, 500);
    const expected = clip(a.expected, 300);
    // The reason and the hope are held to the evidence strictly — a hope with a number in it is a target nobody set —
    // and may name only the action's own days. The instruction may also count small things ("write five letters")
    // and say the owner's minutes.
    const when = new Set([...allowed, String(days), String(Math.ceil(days / 7))]);
    const own = new Set([...when, String(minutes)]);
    const made = [...inventedIn(why, when), ...inventedIn(expected, when), ...inventedIn(`${title} ${detail}`, own, true)];
    if (made.length) { refused.push(`${a.kind}: ${[...new Set(made)].join(", ")} is not in the evidence`); continue; }

    const metric = a.kind === "fix_measurement" ? "gap_closed" : measurable(a.metric, e) && a.metric !== "gap_closed" ? a.metric : metricFor(a.kind, e);
    if (metric !== a.metric && a.kind !== "fix_measurement") refused.push(`${a.kind}: ${a.metric} cannot be counted here, so ${metric}`);

    // Only an action that stood blocks a later one with the same title: a refused first try must not hide a clean second.
    seen.add(key);
    actions.push({
      kind: a.kind, title, detail, why, expected, metric, gap, reviewAfterDays: days, ownerMinutes: minutes, experiment,
      cost: costOf(a.kind, ctx),
    });
  }
  if (!actions.length) actions.push(fallbackAction(e, ctx));
  return { body: { happened, uncertain, diagnosis, actions }, refused };
}

/* ── the owner's part ──────────────────────────────────────────────────────────────────────────────────────── */

/** An owner's change to the recommendation: the words, when it is looked at again, and how it is counted. Not the kind. */
export const ActionEdit = z.object({
  title: z.string().trim().min(1).max(120),
  detail: z.string().trim().max(500).default(""),
  reviewAfterDays: z.number().int().min(7).max(42),
  metric: Metric,
});
export type ActionEdit = z.infer<typeof ActionEdit>;

/**
 * The recommendation as the owner changed it, or why the change cannot stand. The kind stays — it is which tool the
 * yes opens — and so does the price; a test stays a test, held to four weeks.
 */
export function editAction(action: ReviewAction, edit: ActionEdit, e: ReviewEvidence): { action: ReviewAction } | { error: string } {
  if (action.kind === "fix_measurement" && edit.metric !== "gap_closed") return { error: "A fix is counted by whether it is fixed." };
  if (action.kind !== "fix_measurement" && edit.metric === "gap_closed") return { error: "Only a fix is counted by whether it is fixed." };
  if (!measurable(edit.metric, e)) return { error: `Joogoos cannot count ${METRIC_WORDS[edit.metric]} for you yet.` };
  return {
    action: {
      ...action, title: edit.title, detail: edit.detail, metric: edit.metric,
      reviewAfterDays: reviewDays(edit.reviewAfterDays, action.experiment),
    },
  };
}

/* ── the decision and what came of it ─────────────────────────────────────────────────────────────────────── */

export const DecisionState = z.enum(["proposed", "approved", "rejected"]);
export type DecisionState = z.infer<typeof DecisionState>;

/**
 * Where the work is, apart from whether it was agreed: a yes that opened nothing, a project nobody generated, and a
 * fix the owner has not made yet are three different things, and the old proposals table could not tell them apart.
 */
export const ExecutionState = z.enum(["not_started", "started", "waiting_on_you", "done", "failed", "cancelled"]);
export type ExecutionState = z.infer<typeof ExecutionState>;

/** What a yes opened: a Studio project it started, or the tab the owner finishes it on. */
export type ExecutionRef =
  | { kind: "project"; id: string }
  | { kind: "tab"; tab: "Business" | "Studio" | "Customers" | "SEO"; step?: string | undefined };

/** A metric counted over one window. The baseline is one of these, taken when the owner says yes. */
export interface MetricReading { metric: Metric; value: number | null; from: string; to: string; gapOpen?: boolean | undefined }

export const OutcomeVerdict = z.enum(["up", "flat", "down", "too_little", "unmeasurable", "done", "not_yet"]);
export type OutcomeVerdict = z.infer<typeof OutcomeVerdict>;

export interface Outcome {
  verdict: OutcomeVerdict;
  before: MetricReading;
  after: MetricReading;
  /** One sentence, and it says "after", never "because of". */
  says: string;
  /** Other decisions running over the same days: the reason an "after" is not a "because". */
  alongside: string[];
}

/**
 * Whether a fix did what it was for, read off fresh evidence by the state it was meant to bring about — never by the
 * gap's name being gone, which a worse state also does: a Search Console connection revoked rather than repaired
 * stops being `search_broken` by becoming `search_not_connected`.
 */
export function gapClosed(gap: Gap, e: ReviewEvidence): boolean {
  const w = e.website;
  switch (gap) {
    case "website_not_given": return w.state !== "none";
    case "counter_not_installed": return w.state === "counting";
    case "counter_quiet": return w.state === "counting" && (w.lastEventDaysAgo ?? 0) < QUIET_DAYS;
    case "enquiries_none_seen": return w.state === "counting" && w.enquiriesEver > 0;
    case "window_partial": return w.state === "counting" && !w.partial;
    case "goal_not_said": return Boolean(e.goal?.trim());
    case "mailbox_not_connected": return e.outreach.mailbox !== "none";
    case "replies_unseen": return e.outreach.mailbox === "reads_replies" || e.outreach.mailbox === "replies_by_address";
    case "search_not_connected":
    case "search_broken": return e.search.state === "connected";
    case "letters_rehearsed":
    case "post_metrics_missing":
    case "product_events_missing": return !e.gaps.includes(gap);
  }
}

/** Under these, a change between two windows is noise. Per metric, because three enquiries is a lot and three visits is not. */
const SIGNAL: Partial<Record<Metric, number>> = { site_visitors: 20, site_enquiries: 3, letters_sent: 5, replies: 2, meetings: 1, posts_published: 2 };

/**
 * What came of an action, judged by code against the baseline (35 §outcome review). It never claims a cause: the
 * sentence says what moved after the yes, how much, and what else was going on at the same time, and a change too
 * small to tell from luck is said as exactly that.
 */
export function judgeOutcome(before: MetricReading, after: MetricReading, alongside: string[] = []): Outcome {
  const words = METRIC_WORDS[before.metric];
  const also = alongside.length
    ? ` ${alongside.length === 1 ? "Another change was" : `${alongside.length} other changes were`} running over the same days, so this is what happened after it, not proof it was because of it.`
    : " That is what happened after it, which is not the same as because of it.";
  const verdict = (v: OutcomeVerdict, says: string): Outcome => ({ verdict: v, before, after, says, alongside });

  if (before.metric === "gap_closed") {
    return after.gapOpen === false ? verdict("done", "It is fixed: the next review can count what this one could not.") : verdict("not_yet", "It is not fixed yet: the review still cannot count it.");
  }
  // A website count whose days before the yes began before the counter did: half a window of nothing is not a low
  // count, and comparing with it would report a rise on flat traffic.
  if (before.value === null && after.value !== null && (before.metric === "site_visitors" || before.metric === "site_enquiries")) {
    return verdict("unmeasurable", "There is nothing to compare with: your website was not being counted for all of the days before you said yes.");
  }
  if (before.metric === "none" || before.value === null || after.value === null) {
    return verdict("unmeasurable", "Joogoos had no way to count this one. Only you can say whether it worked.");
  }
  const [b, a] = [before.value, after.value];
  const floor = SIGNAL[before.metric] ?? 3;
  // A step of two is noise for most counts, but not for one whose floor is one: none to one booked call moved.
  const step = Math.min(2, floor);
  if (Math.max(a, b) < floor) return verdict("too_little", `${cap(words)}: ${b} before, ${a} after. Too few either way to tell anything from luck.`);
  const moved = `${cap(words)} went from ${b} to ${a}.`;
  if (a >= b * 1.2 && a - b >= step) return verdict("up", moved + also);
  if (a <= b * 0.8 && b - a >= step) return verdict("down", moved + also);
  return verdict("flat", `${cap(words)} stayed about the same: ${b} before, ${a} after.`);
}

const cap = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

/* ── the wire ──────────────────────────────────────────────────────────────────────────────────────────────── */

/** The decision on a review, as the screen draws it. `action` is the one that stands: the owner's edit, or the proposal. */
export interface DecisionView {
  id: string;
  state: DecisionState;
  action: ReviewAction;
  edited: boolean;
  decidedAt: string | null;
  reason: string | null;
  execution: ExecutionState;
  executionRef: ExecutionRef | null;
  /** The day the outcome is looked at, ISO. Set by the yes. */
  reviewOn: string | null;
  outcome: Outcome | null;
}

/** One turn of the conversation about a review (`conversation.ts`). */
export interface TurnView {
  id: string;
  role: "owner" | "joogoos";
  channel: "text" | "voice";
  text: string;
  /** A change Joogoos proposed to the recommendation, for the owner to use or leave. Null on the owner's own turns. */
  change: ActionEdit | null;
  at: string;
}

export interface ReviewView {
  id: string;
  status: "writing" | "ready" | "failed";
  createdAt: string;
  evidence: ReviewEvidence;
  /** The gaps, each with its sentence and where it is fixed, in the evidence's order. */
  gaps: Array<{ gap: Gap; says: string; where: GapWhere }>;
  body: ReviewBody | null;
  error: string | null;
  decision: DecisionView | null;
  turns: TurnView[];
}

/** An earlier decision, for the list under the review: what was decided, and what came of it. */
export interface PastDecision {
  id: string;
  reviewId: string;
  title: string;
  kind: ActionKind;
  state: DecisionState;
  decidedAt: string | null;
  execution: ExecutionState;
  outcome: Outcome | null;
}

/** `GET /v1/review`, and the answer to every write on it, so the screen redraws from one read. */
export interface ReviewResponse {
  /** False on a box with no model configured: the screen says so rather than offering a dead button. */
  available: boolean;
  /** Whether the person reading may decide: the owner only. The screen offers the decision buttons to nobody else. */
  mayDecide: boolean;
  /** What a review costs, with the box's margin, as every paid button's price is. Null when it cannot be priced. */
  priceUsd: string | null;
  /** What one question about a review costs, the same way. */
  turnUsd: string | null;
  latest: ReviewView | null;
  past: PastDecision[];
  /** Whether a new review can be started now, and if not, the sentence saying why. */
  canStart: { ok: boolean; why: string | null };
  limits: { turnsPerReview: number; said: number };
}
